import { toast } from 'react-toastify'

/**
 * Extract a filename from a Content-Disposition header, if present.
 * Falls back to null so the caller can apply its own default.
 */
const filenameFromContentDisposition = (header: string | null): string | null => {
  if (!header) return null
  // Prefer RFC 5987 filename*, then plain filename.
  const utf8Match = header.match(/filename\*=(?:UTF-8'')?["']?([^"';]+)["']?/i)
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1])
  const asciiMatch = header.match(/filename=["']?([^"';]+)["']?/i)
  return asciiMatch?.[1] ?? null
}

export const fetchPackingSlip = async (orderId: string | number, loadingToast: number | string) => {
  try {
    const response = await fetch(`/api/edi/packing-slip/${orderId}/generate`)

    if (!response.ok) {
      const errorData = await response.json()
      toast.dismiss(loadingToast)
      return errorData
    }

    if (response.headers.get('content-type')?.includes('application/pdf')) {
      const blob = await response.blob()
      const fileURL = URL.createObjectURL(blob)
      // Trigger a real download via an anchor click. Using window.open() after an
      // awaited fetch loses the user-gesture context and is blocked by popup blockers,
      // which left the PDF appearing not to download at all.
      const link = document.createElement('a')
      link.href = fileURL
      // Respect the server-provided filename when available, otherwise fall back.
      link.download =
        filenameFromContentDisposition(response.headers.get('content-disposition')) ?? `packing_slip_${orderId}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(fileURL), 1000)
      toast.dismiss(loadingToast)
      // We can only confirm the download was triggered, not that it finished saving.
      toast.success('Packing slip download started')
      return null
    }

    toast.dismiss(loadingToast)
    return await response.json()
  } catch (error) {
    throw error
  }
}

/**
 * Generates a packing slip PDF for an order and downloads it.
 * @param orderId The ID of the order to generate a packing slip for
 */
export const generatePackingSlip = async (orderId: string | number) => {
  const loadingToast = toast.loading('Generating packing slip...')

  try {
    const result = await fetchPackingSlip(orderId, loadingToast)

    if (result === null) {
      return
    }

    if (result && result.status === 'error') {
      toast.error('Cannot generate packing slip. Please contact support.')
      return
    }
  } catch (error) {
    toast.dismiss(loadingToast)
    toast.error('Cannot connect to server. Please try again later.')
  }
}
